// src/pages/MySubscription.jsx
import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import Header from "../components/layout/Header";
import "./my-subscription.css";

const BASE_URL = process.env.REACT_APP_API_URL?.replace(/\/api$/, "") || "https://suvidha-backend-353j.onrender.com";

function authHeaders() {
  const token = localStorage.getItem("token");
  return { "Content-Type": "application/json", ...(token ? { Authorization: `Bearer ${token}` } : {}) };
}

function fmt(d) {
  if (!d) return "—";
  return new Date(d).toLocaleDateString("en-IN", { day: "numeric", month: "long", year: "numeric" });
}

function fmtMoney(n) {
  return new Intl.NumberFormat("en-IN", { style: "currency", currency: "INR" }).format(n || 0);
}

function daysLeft(endDate) {
  if (!endDate) return 0;
  const diff = new Date(endDate) - new Date();
  return Math.max(0, Math.ceil(diff / (1000 * 60 * 60 * 24)));
}

function progressPct(startDate, endDate) {
  if (!startDate || !endDate) return 0;
  const total   = new Date(endDate) - new Date(startDate);
  const elapsed = new Date()        - new Date(startDate);
  return Math.min(100, Math.max(0, Math.round((elapsed / total) * 100)));
}

const PLAN_FEATURES = {
  Silver: ["Weekly wellness check-in calls","Emergency coordination 24/7","Doctor tele-consultation (1x/quarter)","Medication reminders","Priority support access"],
  Gold:   ["All Silver features","Doctor tele-consultation (monthly)","Annual full body health check","Personalized geriatric care plan","Home safety assessment (quarterly)"],
  Platinum: ["All Gold features","Doctor home visits (biannual)","Daily wellness monitoring","Priority caregiver access","Dedicated care manager"],
};

function getPlanFeatures(planName = "") {
  const tier = Object.keys(PLAN_FEATURES).find(k => planName.toLowerCase().includes(k.toLowerCase()));
  return PLAN_FEATURES[tier] || [];
}

export default function MySubscription() {
  const navigate = useNavigate();
  const [data,    setData]    = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const res  = await fetch(`${BASE_URL}/api/subscriptions/my`, { headers: authHeaders() });
        const json = await res.json();
        setData(json);
      } catch {}
      finally { setLoading(false); }
    })();
  }, []);

  const active  = data?.active;
  const history = data?.history || [];
  const left    = daysLeft(active?.end_date);
  const pct     = progressPct(active?.start_date, active?.end_date);
  const features = getPlanFeatures(active?.plan_name || "");

  return (
    <>
      <Header />
      <div className="ms-page">
        <div className="ms-container">

          {/* ── Header ── */}
          <div className="ms-header">
            <div>
              <h1 className="ms-title">My Subscription</h1>
              <p className="ms-subtitle">Manage your care plan and billing</p>
            </div>
            {!active && !loading && (
              <button className="ms-upgrade-btn" onClick={() => navigate("/plans")}>
                ➕ Subscribe Now
              </button>
            )}
          </div>

          {loading ? (
            <div className="ms-loading"><div className="ms-spinner" /><p>Loading...</p></div>
          ) : !active ? (
            /* ── NO ACTIVE SUB ── */
            <div className="ms-empty">
              <div className="ms-empty-icon">💊</div>
              <h2>No Active Subscription</h2>
              <p>Subscribe to a care plan to get priority access to all our elder care services.</p>
              <button className="ms-upgrade-btn" onClick={() => navigate("/plans")}>
                View Plans →
              </button>
            </div>
          ) : (
            /* ── ACTIVE SUB ── */
            <>
              {/* Main card */}
              <div className="ms-card ms-card-active">
                <div className="ms-card-top">
                  <div>
                    <div className="ms-plan-tag">✅ Active Plan</div>
                    <h2 className="ms-plan-name">{active.plan_name}</h2>
                    <div className="ms-plan-billing">
                      {active.billing_cycle === "yearly" ? "Annual" : "Monthly"} billing
                    </div>
                  </div>
                  <div className="ms-card-amount">
                    <div className="ms-amount">{fmtMoney(active.amount_paid)}</div>
                    <div className="ms-amount-label">paid</div>
                  </div>
                </div>

                {/* Progress bar */}
                <div className="ms-progress-wrap">
                  <div className="ms-progress-labels">
                    <span>Started {fmt(active.start_date)}</span>
                    <span className={left <= 7 ? "ms-warn" : ""}>{left} days remaining</span>
                  </div>
                  <div className="ms-progress-bar">
                    <div className="ms-progress-fill" style={{ width: `${pct}%` }} />
                  </div>
                  <div className="ms-progress-labels">
                    <span>{pct}% used</span>
                    <span>Expires {fmt(active.end_date)}</span>
                  </div>
                </div>

                {/* Info grid */}
                <div className="ms-info-grid">
                  <div className="ms-info-item">
                    <div className="ms-info-icon">📅</div>
                    <div>
                      <div className="ms-info-label">Start Date</div>
                      <div className="ms-info-val">{fmt(active.start_date)}</div>
                    </div>
                  </div>
                  <div className="ms-info-item">
                    <div className="ms-info-icon">⏰</div>
                    <div>
                      <div className="ms-info-label">Expiry Date</div>
                      <div className="ms-info-val">{fmt(active.end_date)}</div>
                    </div>
                  </div>
                  <div className="ms-info-item">
                    <div className="ms-info-icon">💳</div>
                    <div>
                      <div className="ms-info-label">Payment</div>
                      <div className="ms-info-val ms-paid">✓ Paid</div>
                    </div>
                  </div>
                  <div className="ms-info-item">
                    <div className="ms-info-icon">⚡</div>
                    <div>
                      <div className="ms-info-label">Activated</div>
                      <div className="ms-info-val">Instantly</div>
                    </div>
                  </div>
                </div>

                {/* Expiry warning */}
                {left <= 7 && left > 0 && (
                  <div className="ms-expiry-warn">
                    ⚠️ Your subscription expires in <strong>{left} days</strong>.
                    <button onClick={() => navigate("/plans")}>Renew Now →</button>
                  </div>
                )}
              </div>

              {/* Plan features */}
              {features.length > 0 && (
                <div className="ms-features">
                  <h3>What's included in your plan</h3>
                  <div className="ms-features-grid">
                    {features.map((f, i) => (
                      <div key={i} className="ms-feature-item">
                        <span className="ms-feature-check">✓</span>
                        <span>{f}</span>
                      </div>
                    ))}
                  </div>
                  <button className="ms-book-btn" onClick={() => navigate("/book-service")}>
                    📋 Book a Service
                  </button>
                </div>
              )}
            </>
          )}

          {/* ── Subscription History ── */}
          {history.length > 0 && (
            <div className="ms-history">
              <h3>Subscription History</h3>
              <div className="ms-history-list">
                {history.map(h => (
                  <div key={h.id} className={`ms-history-row ${h.status}`}>
                    <div className="ms-history-left">
                      <div className="ms-history-plan">{h.plan_name}</div>
                      <div className="ms-history-dates">{fmt(h.start_date)} → {fmt(h.end_date)}</div>
                    </div>
                    <div className="ms-history-right">
                      <div className="ms-history-amount">{fmtMoney(h.amount_paid)}</div>
                      <span className={`ms-history-badge ms-badge-${h.status}`}>
                        {h.status === "active" ? "✅ Active" : h.status === "expired" ? "⌛ Expired" : "🚫 Cancelled"}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

        </div>
      </div>
    </>
  );
}